import { useState, useEffect, useCallback } from 'react';
import {
  Box, Title, Text, SimpleGrid, Paper, Group, Loader, Alert,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useAuth } from '../../shared/contexts/AuthContext';
import { ivrStats } from './api_scripts/ivrClient';

const COMPLEXITY_COLORS = {
  S:  { bg: '#ebfbee', color: '#2f9e44', border: '#b2f2bb' },
  M:  { bg: '#e7f5ff', color: '#1971c2', border: '#a5d8ff' },
  L:  { bg: '#fff9db', color: '#e67700', border: '#ffec99' },
  XL: { bg: '#fff5f5', color: '#c92a2a', border: '#ffc9c9' },
};

function MetricCard({ label, value }) {
  return (
    <Paper withBorder p="lg" radius="md">
      <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={6} style={{ letterSpacing: '0.5px' }}>
        {label}
      </Text>
      <Text size="2rem" fw={700} c="dark">
        {value ?? '—'}
      </Text>
    </Paper>
  );
}

function BreakdownCard({ title, data }) {
  if (!data || typeof data !== 'object') return null;
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const max = entries[0]?.[1] || 1;

  return (
    <Paper withBorder p="lg" radius="md">
      <Text fw={600} mb="md" size="sm">{title}</Text>
      <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {entries.map(([key, count]) => (
          <Box key={key}>
            <Group justify="space-between" mb={3}>
              <Text size="xs" style={{ color: '#495057' }}>{key}</Text>
              <Text size="xs" fw={600} c="dimmed">{count}</Text>
            </Group>
            <Box style={{
              height: 4, borderRadius: 2, background: '#e9ecef', overflow: 'hidden',
            }}>
              <Box style={{
                height: '100%',
                width: `${(count / max) * 100}%`,
                background: '#4263eb',
                borderRadius: 2,
              }} />
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

export default function IvrStats() {
  const { getApiToken } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setStats(await ivrStats(getApiToken));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getApiToken]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box style={{ display: 'flex', flexDirection: 'column' }}>

      <Box style={{ padding: '32px 40px 24px', borderBottom: '1px solid #e9ecef', background: '#ffffff' }}>
        <Title order={2} size="1.5rem" fw={700}>Statistics</Title>
        <Text c="dimmed" size="sm" mt={4}>Aggregate data across all indexed documents</Text>
      </Box>

      <Box style={{ padding: '32px 40px' }}>
        {loading && <Loader color="indigo" />}

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">{error}</Alert>
        )}

        {stats && (
          <Box>
            <SimpleGrid cols={{ base: 1, sm: 3 }} mb="xl">
              <MetricCard label="Total Use Cases" value={stats.total_use_cases} />
              <MetricCard label="Total Documents" value={stats.total_documents} />
              <MetricCard label="Infrastructure Only" value={stats.infrastructure_only} />
            </SimpleGrid>

            <SimpleGrid cols={{ base: 1, sm: 2 }} mb="xl">
              <BreakdownCard title="By Vertical" data={stats.by_vertical} />
              <BreakdownCard title="By Platform" data={stats.by_platform} />
            </SimpleGrid>

            {stats.by_complexity && Object.keys(stats.by_complexity).length > 0 && (
              <Paper withBorder p="lg" radius="md">
                <Text fw={600} mb="md" size="sm">By Complexity</Text>
                <Group gap="xl" mb="lg">
                  {Object.entries(stats.by_complexity).map(([key, count]) => {
                    const cx = COMPLEXITY_COLORS[key];
                    return (
                      <Box key={key} style={{ textAlign: 'center' }}>
                        <Box style={{
                          width: 36, height: 36, borderRadius: '50%',
                          background: cx?.bg || '#f1f3f5',
                          border: `1px solid ${cx?.border || '#dee2e6'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 13, fontWeight: 700,
                          color: cx?.color || '#495057',
                          margin: '0 auto 6px',
                        }}>
                          {key}
                        </Box>
                        <Text size="sm" fw={600}>{count}</Text>
                      </Box>
                    );
                  })}
                </Group>

                <Box style={{ borderTop: '1px solid #e9ecef', paddingTop: 16 }}>
                  <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={10} style={{ letterSpacing: '0.5px' }}>
                    How complexity is estimated
                  </Text>
                  <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      { key: 'S',  label: 'Simple',     desc: 'Single web service call — one integration, minimal branching.' },
                      { key: 'M',  label: 'Medium',     desc: '2–3 steps with 1–2 integrations — moderate call flow logic.' },
                      { key: 'L',  label: 'Large',      desc: 'Complex branching with multiple integrations — conditional paths, several backend systems.' },
                      { key: 'XL', label: 'Extra Large', desc: 'Payment processing or multi-system real-time transactions — highest integration depth and risk.' },
                    ].map(({ key, label, desc }) => {
                      const cx = COMPLEXITY_COLORS[key];
                      return (
                        <Box key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                          <Box style={{
                            width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                            background: cx?.bg || '#f1f3f5',
                            border: `1px solid ${cx?.border || '#dee2e6'}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 10, fontWeight: 700, color: cx?.color || '#495057',
                          }}>
                            {key}
                          </Box>
                          <Box>
                            <Text size="xs" fw={600} style={{ color: cx?.color || '#495057' }}>{label}</Text>
                            <Text size="xs" c="dimmed">{desc}</Text>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Paper>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
